import { inject, Injectable } from '@angular/core';
import Amplify, { Auth } from 'aws-amplify';
import { Subject } from 'rxjs';
import {
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
  ICognitoUserAttributeData,
} from 'amazon-cognito-identity-js';
import { IUserAttributes } from '@shared/components/profile-dropdown/profile-dropdown.component';
import { ApiConfig } from './api.config';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ResetPasswordModalComponent } from '../pages/auth/reset-password-modal/reset-password-modal.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MessageToasterComponent, ToasterType } from '@shared/components/message-toaster/message-toaster.component';
import { IProfileUser } from '../pages/user-profile/user-profile.component';

export interface IUser {
  email: string;
  password: string;
  showPassword?: boolean;
  code: string;
  name?: string;
  givenName?: string;
  familyName?: string;
  location?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  user = new Subject<IUserAttributes | null>();
  refreshToken!: CognitoRefreshToken;
  configService = inject(ApiConfig);
  readonly dialog = inject(MatDialog);
  snackBar = inject(MatSnackBar);
  authData!: CognitoUserSession;
  config: any;
  domain!: string;
  clientId!: string;
  redirectUri!: string;

  constructor(private router: Router) {}

  // Called in app.component after we get data from config.json file
  setupAmplify() {
    Amplify.configure({
      Auth: this.configService.config.cognito,
    });
  }

  setConfig() {
    this.config = this.configService.config;
    this.domain = this.config.cognito.oauth.domain;
    this.clientId = this.config.cognito.userPoolWebClientId;
    this.redirectUri = this.config.cognito.oauth.redirectSignIn;
    this.setupAmplify();
  }

  public signUp(user: IUser): Promise<any> {
    return Auth.signUp({
      username: user.email,
      password: user.password,
      attributes: {
        email: user.email,
        given_name: user.givenName,
        family_name: user.familyName,
        'custom:location': user.location,
      },
    });
  }

  public confirmSignUp(email: string, code: string): Promise<any> {
    return Auth.confirmSignUp(email, code);
  }

  public fetchAuthSession(): Promise<CognitoUserSession> {
    return Auth.currentSession();
  }

  public resendCode(email: string): Promise<any> {
    return Auth.resendSignUp(email);
  }

  public getUser(): Promise<any> {
    return Auth.currentUserInfo();
  }

  public updateUserData(newUserData: IProfileUser): Promise<any> {
    return Auth.currentUserPoolUser().then(cognitoUser => {
      const attr: ICognitoUserAttributeData[] = [
        { Name: 'family_name', Value: newUserData.family_name.toString() },
        { Name: 'given_name', Value: newUserData.given_name.toString() },
        { Name: 'custom:location', Value: newUserData['custom:location'].toString() },
        { Name: 'custom:preferred_content', Value: newUserData['custom:preferred_content'].toString() },
      ];


      cognitoUser.updateAttributes(attr, (err: any, result: any, details: any) => {
        if (!err && result === 'SUCCESS') {
          this.openMessage(ToasterType.SUCCESS, `The changes have been saved`);
          this.getUser().then(user => {
            this.user.next(user);
          });
        } else {
          this.openMessage(ToasterType.ERROR, `The changes have not been saved`);
        }
      });


    });
  }

  public signIn(email: string, password: string): Promise<CognitoUser> {
    return Auth.signIn(email, password);
  }

  public signOut(): Promise<any> {
    return Auth.signOut();
  }

  public deleteAccount(): Promise<any> {
    return Auth.deleteUser().then(user => {
      this.signOut();
      this.user.next(null);
      this.router.navigate(['/']);
      this.openMessage(ToasterType.SUCCESS, `User deleted`);
    }).catch(error => {
      this.openMessage(ToasterType.ERROR, error);
    });

  }

  public forgotPassword(email: string): Promise<any> {
    return Auth.forgotPassword(email);
  }

  public forgotPasswordSubmit(email: string, code: string, newPassword: string): Promise<any> {
    return Auth.forgotPasswordSubmit(email, code, newPassword);
  }

  public changePassword(oldPassword: string, newPassword: string) {
    return Auth.currentUserPoolUser().then(cognitoUser => {
      Auth.changePassword(cognitoUser, oldPassword, newPassword).then(user => {
        this.openMessage(ToasterType.SUCCESS, `The changes have been saved`);
      }).catch(error => {
        this.openMessage(ToasterType.ERROR, error);
      });
    });
  }

  public setSession(session: CognitoUserSession) {
    this.authData = session;
  }

  public handleResetPassword(userEmail: string, authorizationCode: string) {
    this.dialog.open(ResetPasswordModalComponent,
      {
        panelClass: 'custom-dialog', data: { userEmail, authorizationCode },
      },
    );
  }

  // GET request to receive CODE param
  public handleMicrosoftSignIn() {
    const responseType = 'CODE';
    const oauthUrl = `https://${this.domain}/oauth2/authorize?identity_provider=AzureAD&redirect_uri=${this.redirectUri}&response_type=${responseType}&client_id=${this.clientId}&scope=aws.cognito.signin.user.admin%20email%20openid%20profile`;
    window.location.assign(oauthUrl);
  };

  // POST request that uses CODE to get TOKEN
  public async handleAuthCode() {
    const urlParams = new URLSearchParams(window.location.search);
    const authorizationCode = urlParams.get('code');
    const userEmail = urlParams.get('email');

    // if url contains both email and auth code => open reset password modal
    if (userEmail && authorizationCode) {
      this.handleResetPassword(userEmail, authorizationCode);
      // if only auth code => authorize user
    } else if (authorizationCode) {
      try {
        const tokenUrl = `https://${this.domain}/oauth2/token`;

        const body = new URLSearchParams({
          grant_type: 'authorization_code',
          code: authorizationCode,
          client_id: this.clientId,
          redirect_uri: this.redirectUri,
        });

        const response = await fetch(tokenUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: body.toString(),
        });

        // @ts-ignore
        const data = await response.json();
        console.log('Tokens:', data);

        if (data.id_token) {
          await this.setAuthSession(data);
        }

      } catch (error) {
        console.error('Error exchanging code for tokens:', error);
      }
    }
  }

  openMessage(type: ToasterType, message: string, title?: string) {
    this.snackBar.openFromComponent(MessageToasterComponent, {
      data: {
        message: message,
        title: title,
      },
      panelClass: type,
      duration: 5000,
    });
  }

  private async setAuthSession(tokenData: any) {
    const { id_token, access_token, refresh_token, expires_in } = tokenData;

    const idToken = new CognitoIdToken({ IdToken: id_token });
    const accessToken = new CognitoAccessToken({ AccessToken: access_token });
    const refreshToken = new CognitoRefreshToken({ RefreshToken: refresh_token });

    const sessionData = {
      IdToken: idToken,
      AccessToken: accessToken,
      RefreshToken: refreshToken,
      ClockDrift: 0,
    };

    const session = new CognitoUserSession(sessionData);

    const userPool = new CognitoUserPool({
      UserPoolId: this.config.cognito.userPoolId,
      ClientId: this.config.cognito.userPoolWebClientId,
    });

    const userData = {
      Username: idToken.payload['sub'],
      Pool: userPool,
    };

    const cognitoUser = new CognitoUser(userData);
    cognitoUser.setSignInUserSession(session);

    // Updating attribute to set MSAL authentication as internal user
    const attr: ICognitoUserAttributeData[] = [{
      Name: 'custom:internal',
      Value: 'true',
    }];
    cognitoUser.updateAttributes(attr, () => {});

    this.getUser().then(user => {
      this.user.next(user);
    });
  }
}
